



import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Settings, ClipboardList, Plus, Trash2, ChevronLeft, AlertCircle, CheckCircle2, Upload, PenLine, BookmarkPlus, LayoutTemplate, Pencil, X, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
import { Badge } from './Badge';
import TextInput from './TextInput';
import TextArea from './TextArea';
import Button from './Button';
import { CustomSelect, Select, SelectItem } from './Select';
import RubricUploadAssistant from './RubricUploadAssistant';
import ConfirmModal from './ConfirmModal';
import SaveRubricTemplateModal from './SaveRubricTemplateModal';
import { DEFAULT_SYSTEM_INSTRUCTIONS } from '../constants';
import { transcribeImage } from '../services/aiService';
import { RubricCriterion } from '../types';
import { StructuredRubricResult } from '../services/rubricImportService';
import { useAssessmentStore } from '../src/store/assessmentStore';
import { Assessment, RubricTemplate, RubricTemplateCriterion } from '../src/types';

interface ExemplarImage {
  id?: string;
  pageLabel: string;
  base64?: string;
  previewUrl?: string;
}

interface Exemplar {
  id: string;
  essayText: string;
  criteriaBands: Array<{
    id: string;
    name: string;
    band: string;
  }>;
  overallScore: string;
  explanation: string;
  stagedImages: ExemplarImage[];
}

interface RubricImportSnapshot {
  processedAt: string;
  summary: string[];
  warnings: string[];
  files: string[];
  preview: string;
}

type RubricMode = 'choice' | 'manual' | 'upload' | 'template';

interface CreateAssessmentFormProps {
  onSave: (assessment: any) => Promise<void>;
  onCancel: () => void;
  existingAssessment?: Assessment | null;
}

const sortImagesByPageLabel = (images: ExemplarImage[]) => {
  const extractPageNumber = (label: string) => {
    const cleanedLabel = String(label).toLowerCase().replace('page', '').trim();
    const num = parseInt(cleanedLabel, 10);
    return isNaN(num) ? Infinity : num;
  };

  return [...images].sort((a, b) => {
    const pageNumA = extractPageNumber(a.pageLabel);
    const pageNumB = extractPageNumber(b.pageLabel);

    if (pageNumA !== Infinity && pageNumB !== Infinity) {
      if (pageNumA !== pageNumB) return pageNumA - pageNumB;
      return a.pageLabel.localeCompare(b.pageLabel);
    }
    if (pageNumA !== Infinity) return -1; 
    if (pageNumB !== Infinity) return 1;  
    return a.pageLabel.localeCompare(b.pageLabel); 
  });
};


const generateRubricStringFromCriteria = (criteria: RubricCriterion[], totalMarks: number | ''): string => {
    if (!criteria || criteria.length === 0 || !totalMarks) return '';
    let rubricString = `This assessment is marked out of a total of ${totalMarks} marks.\n\n`;

    criteria.forEach(criterion => {
        rubricString += `${criterion.name} (Weight: ${criterion.weight}%)\n`;
        const sortedBands = [...criterion.bands].sort((a, b) => (Number(a.min) || 0) - (Number(b.min) || 0));
        sortedBands.forEach(band => {
            rubricString += `- Band (${band.min}-${band.max}): ${band.descriptor}\n`;
        });
        rubricString += '\n';
    });
    return rubricString.trim();
};

// Default values for dropdowns
const DEFAULT_SUBJECTS = ['History', 'Modern History', 'Ancient History', 'Extension History'];
const DEFAULT_TOPICS = ['Russian & the Soviet Union', 'World War I', 'World War II', 'Cold War', 'Ancient Greece', 'Ancient Rome', 'Medieval Europe', 'Renaissance', 'Industrial Revolution', 'American Revolution', 'French Revolution', 'Australian History', 'Indigenous History'];
const DEFAULT_YEAR_GROUPS = ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12'];
const DEFAULT_EXAM_CONDITIONS = ['In Class', 'Take Home'];
const DEFAULT_ASSESSMENT_TYPES = ['Short Answer', 'Major Work', 'Source Analysis', 'Essay', 'Essay Using Sources', 'Combination'];

// Generate year options (current year and 30 years prior)
const generateYearOptions = (): string[] => {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let i = 0; i <= 30; i++) {
    years.push((currentYear - i).toString());
  }
  return years;
};

const YEAR_OPTIONS = generateYearOptions();


const CreateAssessmentForm: React.FC<CreateAssessmentFormProps> = ({ onSave, onCancel, existingAssessment }) => {
  const customValues = useAssessmentStore(state => state.customValues);
  const addCustomValue = useAssessmentStore(state => state.addCustomValue);
  const rubricTemplates = useAssessmentStore(state => state.rubricTemplates);
  const fetchRubricTemplates = useAssessmentStore(state => state.fetchRubricTemplates);
  const renameRubricTemplate = useAssessmentStore(state => state.renameRubricTemplate);
  const deleteRubricTemplate = useAssessmentStore(state => state.deleteRubricTemplate);

  const [name, setName] = useState('');
  const [question, setQuestion] = useState('');
  const [yearLevel, setYearLevel] = useState('');
  const [systemInstructions, setSystemInstructions] = useState(DEFAULT_SYSTEM_INSTRUCTIONS);
  const [totalMarks, setTotalMarks] = useState<number | ''>('');
  const [criteria, setCriteria] = useState<RubricCriterion[]>([]);
  const [exemplars, setExemplars] = useState<Exemplar[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // New fields
  const [subject, setSubject] = useState('History');
  const [topic, setTopic] = useState('Major Work');
  const [year, setYear] = useState('');
  const [examCondition, setExamCondition] = useState('');
  const [assessmentType, setAssessmentType] = useState('Short Answer');
  const [minWordCount, setMinWordCount] = useState<number | ''>('');
  const [maxWordCount, setMaxWordCount] = useState<number | ''>('');
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  
  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    question?: string;
    yearLevel?: string;
    totalMarks?: string;
    weights?: string;
    coverage?: { criterionId: string; message: string; }[];
    subject?: string;
    examCondition?: string;
    assessmentType?: string;
    minWordCount?: string;
    maxWordCount?: string;
  }>({});
  const [rubricImportSnapshot, setRubricImportSnapshot] = useState<RubricImportSnapshot | null>(null);
  const [showRubricImportPreview, setShowRubricImportPreview] = useState(false);
  const [rubricMode, setRubricMode] = useState<RubricMode>(existingAssessment ? 'manual' : 'choice');

  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [initializedAssessmentId, setInitializedAssessmentId] = useState<string | null>(null);

  // Rubric template state
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);
  const [isManagingTemplates, setIsManagingTemplates] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingTemplateName, setEditingTemplateName] = useState('');
  const [templateManageError, setTemplateManageError] = useState<string | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<RubricTemplate | null>(null);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);

  useEffect(() => {
    fetchRubricTemplates();
  }, [fetchRubricTemplates]);

  useEffect(() => {
    if (existingAssessment && existingAssessment.id !== initializedAssessmentId) {
        setName(existingAssessment.name || '');
        setQuestion(existingAssessment.question || '');
        setYearLevel(existingAssessment.yearLevel || '');
        setSystemInstructions(existingAssessment.systemInstructions ?? '');
        setTotalMarks(existingAssessment.totalMarks || '');
        
        // Set new fields from existing assessment
        setSubject(existingAssessment.subject || 'History');
        setTopic(existingAssessment.topic || 'Major Work');
        setYear(existingAssessment.year || '');
        setExamCondition(existingAssessment.examCondition || '');
        setAssessmentType(existingAssessment.assessmentType || 'Short Answer');
        setMinWordCount(existingAssessment.minWordCount || '');
        setMaxWordCount(existingAssessment.maxWordCount || '');

        if (existingAssessment.criteria && Array.isArray(existingAssessment.criteria)) {
            const criteriaWithIds = existingAssessment.criteria.map((c: any, cIdx: number) => ({
                ...c,
                id: `crit-${cIdx}-${Date.now()}`,
                bands: (c.bands || []).map((b: any, bIdx: number) => ({
                    ...b,
                    id: `band-${bIdx}-${cIdx}-${Date.now()}`
                }))
            }));
            setCriteria(criteriaWithIds);
        } else {
            setCriteria([]);
        }

        const exemplarsWithIds = (existingAssessment.exemplars || []).map((ex: any, index: number) => ({
            ...ex,
            id: `ex-${index}-${Date.now()}`,
            criteriaBands: (ex.criteriaBands || []).map((cb: any, critIndex: number) => ({
                ...cb,
                id: `cb-${critIndex}-${index}-${Date.now()}`
            })),
            stagedImages: [],
            overallScore: String(ex.overallScore || ''),
            explanation: ex.explanation || ''
        }));
        setExemplars(exemplarsWithIds);
        setRubricMode('manual');
        setRubricImportSnapshot(null);
        setShowRubricImportPreview(false);
        setInitializedAssessmentId(existingAssessment.id || null);
    } else if (!existingAssessment && initializedAssessmentId !== null) {
        setName('');
        setQuestion('');
        setYearLevel('');
        setSystemInstructions(DEFAULT_SYSTEM_INSTRUCTIONS);
        setTotalMarks('');
        setCriteria([]);
        setExemplars([]);
        setRubricMode('choice');
        setRubricImportSnapshot(null);
        setShowRubricImportPreview(false);
        setInitializedAssessmentId(null);
    }
  }, [existingAssessment, initializedAssessmentId]);

  const handleCriterionChange = (critId: string, field: string, value: any) => {
    setCriteria(criteria.map(c => c.id === critId ? { ...c, [field]: value } : c));
  };

  const handleAddCriterion = () => {
    const newId = `crit-${criteria.length}-${Date.now()}`;
    const newBandId = `band-0-${newId}`;
    setCriteria([...criteria, {
      id: newId,
      name: '',
      weight: '',
      bands: [{ id: newBandId, min: '', max: '', descriptor: '' }]
    }]);
  };

  const handleRemoveCriterion = (critId: string) => {
    setCriteria(criteria.filter(c => c.id !== critId));
  };
  
  const handleBandChange = (critId: string, bandId: string, field: string, value: any) => {
    setCriteria(criteria.map(c => {
      if (c.id === critId) {
        const newBands = c.bands.map(b => b.id === bandId ? { ...b, [field]: value } : b);
        return { ...c, bands: newBands };
      }
      return c;
    }));
  };

  const handleAddBand = (critId: string) => {
    setCriteria(criteria.map(c => {
      if (c.id === critId) {
        const newBandId = `band-${c.bands.length}-${critId}`;
        return { ...c, bands: [...c.bands, { id: newBandId, min: '', max: '', descriptor: '' }] };
      }
      return c;
    }));
  };

  const handleRemoveBand = (critId: string, bandId: string) => {
    setCriteria(criteria.map(c => {
      if (c.id === critId) {
        if (c.bands.length > 1) {
            return { ...c, bands: c.bands.filter(b => b.id !== bandId) };
        }
      }
      return c;
    }));
  };

  const handleRubricUploadResult = (result: StructuredRubricResult) => {
    setCriteria(result.criteria);
    setRubricImportSnapshot({
      processedAt: new Date().toISOString(),
      summary: result.summary || [],
      warnings: result.warnings || [],
      files: result.fileSummaries.map(file => file.name),
      preview: result.extractedText,
    });
    setShowRubricImportPreview(false);
    setValidationErrors(prev => ({
      ...prev,
      weights: undefined,
      coverage: undefined,
    }));
  };

  // --- Rubric template helpers ---

  const selectedTemplate = rubricTemplates.find(t => t.id === selectedTemplateId) || null;

  // Snapshot copy: regenerate UI ids the same way the existingAssessment effect does.
  const applyTemplate = (template: RubricTemplate) => {
    const now = Date.now();
    const copiedCriteria: RubricCriterion[] = template.criteria.map((c, cIdx) => ({
      id: `crit-${cIdx}-${now}`,
      name: c.name,
      weight: c.weight,
      bands: c.bands.map((b, bIdx) => ({
        id: `band-${bIdx}-${cIdx}-${now}`,
        min: b.min,
        max: b.max,
        descriptor: b.descriptor
      }))
    }));
    setCriteria(copiedCriteria);
    setTotalMarks(template.totalMarks);
    setRubricImportSnapshot(null);
    setShowRubricImportPreview(false);
    setValidationErrors(prev => ({
      ...prev,
      totalMarks: undefined,
      weights: undefined,
      coverage: undefined,
    }));
    setRubricMode('manual');
  };

  const handleApplyTemplateClick = () => {
    if (!selectedTemplate) return;
    if (criteria.length > 0) {
      setShowApplyConfirm(true);
    } else {
      applyTemplate(selectedTemplate);
    }
  };

  // Persisted shape (no UI ids), same normalization as finalCriteria in handleSubmit
  const templateCriteriaFromForm = useMemo<RubricTemplateCriterion[]>(() => criteria.map(c => ({
    name: c.name.trim(),
    weight: Number(c.weight) || 0,
    bands: c.bands.map(b => ({
      min: Number(b.min) || 0,
      max: Number(b.max) || 0,
      descriptor: b.descriptor
    }))
  })), [criteria]);

  // Mirrors the submit-time criteria validation: weights sum to 100 and bands cover 1..totalMarks
  const canSaveCriteriaAsTemplate = useMemo(() => {
    if (criteria.length === 0) return false;
    if (!totalMarks || Number(totalMarks) <= 0) return false;
    if (criteria.some(c => !c.name.trim())) return false;
    const totalWeight = criteria.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);
    if (totalWeight !== 100) return false;
    for (const c of criteria) {
      const bands = c.bands
        .map(b => ({ min: Number(b.min), max: Number(b.max), descriptor: b.descriptor }))
        .sort((a, b) => a.min - b.min);
      if (bands.length === 0) return false;
      if (bands[0].min !== 1) return false;
      if (bands[bands.length - 1].max !== Number(totalMarks)) return false;
      for (let i = 0; i < bands.length - 1; i++) {
        if (bands[i].max + 1 !== bands[i + 1].min) return false;
      }
      if (bands.some(b => !b.descriptor.trim())) return false;
    }
    return true;
  }, [criteria, totalMarks]);

  const startRenameTemplate = (template: RubricTemplate) => {
    setEditingTemplateId(template.id);
    setEditingTemplateName(template.name);
    setTemplateManageError(null);
  };

  const cancelRenameTemplate = () => {
    setEditingTemplateId(null);
    setEditingTemplateName('');
    setTemplateManageError(null);
  };

  const submitRenameTemplate = async () => {
    if (!editingTemplateId) return;
    const trimmed = editingTemplateName.trim();
    if (!trimmed) {
      setTemplateManageError('Template name cannot be empty.');
      return;
    }
    const duplicate = rubricTemplates.find(
      t => t.id !== editingTemplateId && t.name.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (duplicate) {
      setTemplateManageError(`A template named "${trimmed}" already exists.`);
      return;
    }
    try {
      await renameRubricTemplate(editingTemplateId, trimmed);
      cancelRenameTemplate();
    } catch (err) {
      setTemplateManageError(err instanceof Error ? err.message : 'Failed to rename template.');
    }
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    setIsDeletingTemplate(true);
    try {
      await deleteRubricTemplate(templateToDelete.id);
      if (selectedTemplateId === templateToDelete.id) {
        setSelectedTemplateId('');
      }
      setTemplateToDelete(null);
    } catch (err) {
      setTemplateManageError(err instanceof Error ? err.message : 'Failed to delete template.');
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const renderCriteriaErrorsAndList = () => (
    <>
      {validationErrors.weights && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded" role="alert">
            {validationErrors.weights}
        </div>
      )}

      {validationErrors.coverage && Array.isArray(validationErrors.coverage) && validationErrors.coverage.length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded space-y-2" role="alert">
              <p className="font-semibold">Rubric Coverage Issues:</p>
              <ul className="list-disc list-inside space-y-1">
                  {validationErrors.coverage.map((err: any, idx: number) => (
                      <li key={idx}>{err.message}</li>
                  ))}
              </ul>
          </div>
      )}

      <div className="space-y-4">
          {criteria.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-secondary-300 bg-secondary-50/80 p-6 text-center text-secondary-700">
              No criteria added yet. Use &ldquo;Add Criterion&rdquo; to start building the rubric.
            </div>
          ) : (
            criteria.map((criterion, critIndex) => {
                const bandRangeText = criterion.bands.some(b => b.min && b.max) ? 
                  `${Math.min(...criterion.bands.filter(b => b.min).map(b => Number(b.min) || 0)) || 1} - ${Math.max(...criterion.bands.filter(b => b.max).map(b => Number(b.max) || 0) || [Number(totalMarks) || 0])}` 
                  : `Min/Max`;
                return (
                    <div key={criterion.id} className="p-5 border border-secondary-200 rounded-2xl bg-secondary-50/80 shadow-inner space-y-4">
                        <div className="flex flex-wrap justify-between items-center gap-3">
                            <div className="space-y-1">
                                <h4 className="text-lg font-semibold text-secondary-900">Criterion {critIndex + 1}</h4>
                                <p className="text-sm text-secondary-600">Band Coverage: {bandRangeText}</p>
                            </div>
                            <Button type="button" onClick={() => handleRemoveCriterion(criterion.id)} variant="danger" size="sm" disabled={criteria.length <= 1 || isSaving} className="ml-2" aria-label={`Remove Criterion ${critIndex + 1}`}>&times; Remove</Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div className="md:col-span-2">
                                <TextInput label="Criterion Name" id={`crit-name-${criterion.id}`} value={criterion.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCriterionChange(criterion.id, 'name', e.target.value)} placeholder="e.g., Historical Knowledge & Evidence" required disabled={isSaving} />
                            </div>
                            <TextInput label="Weight (%)" id={`crit-weight-${criterion.id}`} type="number" value={criterion.weight} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCriterionChange(criterion.id, 'weight', e.target.value === '' ? '' : Number(e.target.value))} placeholder="e.g., 40" required disabled={isSaving} />
                            <div className="md:col-span-2">
                                <div className="p-3 rounded-lg bg-white border border-secondary-200 text-sm text-secondary-700">
                                    Ensure bands below cover 1 to total marks with no gaps.
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 space-y-3">
                             {criterion.bands.map((band, bandIndex) => (
                                <div key={band.id} className="p-3 border border-secondary-200 rounded-xl bg-white shadow-sm">
                                    <div className="flex flex-wrap justify-between items-center mb-2 gap-2">
                                        <h5 className="text-md font-semibold text-secondary-900">Band {bandIndex + 1}</h5>
                                        <Button type="button" onClick={() => handleRemoveBand(criterion.id, band.id)} variant="danger" size="sm" disabled={criterion.bands.length <= 1 || isSaving} aria-label={`Remove Band ${bandIndex + 1}`}>&times;</Button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                                        <TextInput label="Min" id={`band-min-${band.id}`} type="number" value={band.min} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleBandChange(criterion.id, band.id, 'min', e.target.value === '' ? '' : Number(e.target.value))} placeholder="e.g., 1" required disabled={isSaving} />
                                        <TextInput label="Max" id={`band-max-${band.id}`} type="number" value={band.max} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleBandChange(criterion.id, band.id, 'max', e.target.value === '' ? '' : Number(e.target.value))} placeholder="e.g., 5" required disabled={isSaving} />
                                        <div className="md:col-span-4">
                                            <TextArea label="Band Descriptor" id={`band-desc-${band.id}`} value={band.descriptor} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleBandChange(criterion.id, band.id, 'descriptor', e.target.value)} rows={2} placeholder="Enter band descriptor..." required disabled={isSaving} />
                                        </div>
                                    </div>
                                </div>
                             ))}
                             <Button type="button" onClick={() => handleAddBand(criterion.id)} variant="secondary" size="sm" disabled={isSaving}>+ Add Band</Button>
                        </div>
                    </div>
                )
            })
          )}
      </div>
    </>
  );

  const renderCriteriaHeader = (title: string, subtitle: string) => (
    <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
      <div>
        <h4 className="text-lg font-semibold text-secondary-900">{title}</h4>
        <p className="text-sm text-secondary-600">{subtitle}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          onClick={() => setShowSaveTemplateModal(true)}
          variant="secondary"
          disabled={isSaving || !canSaveCriteriaAsTemplate}
          title={canSaveCriteriaAsTemplate
            ? 'Save the current criteria as a reusable template'
            : 'Complete the rubric first: weights must sum to 100% and bands must cover 1 to total marks'}
          className="gap-1.5"
        >
          <BookmarkPlus className="h-4 w-4" />
          Save as Template
        </Button>
        <Button type="button" onClick={handleAddCriterion} variant="secondary" disabled={isSaving}>
          + Add Criterion
        </Button>
      </div>
    </div>
  );

  const renderImportSnapshot = () => {
    if (!rubricImportSnapshot) return null;
    return (
      <div className="space-y-3 rounded-2xl border border-emerald-200 bg-emerald-50/80 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-semibold text-emerald-900">Rubric auto-filled from uploaded files</p>
            <p className="text-sm text-emerald-800">
              Applied {new Date(rubricImportSnapshot.processedAt).toLocaleString()} &middot; Files:{' '}
              {rubricImportSnapshot.files.join(', ')}
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setRubricImportSnapshot(null);
              setShowRubricImportPreview(false);
            }}
          >
            Dismiss
          </Button>
        </div>

        {rubricImportSnapshot.summary.length > 0 && (
          <div className="space-y-1 text-sm text-secondary-800">
            {rubricImportSnapshot.summary.map((line, idx) => (
              <p key={`import-summary-${idx}`}>&bull; {line}</p>
            ))}
          </div>
        )}

        {rubricImportSnapshot.warnings.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-1">
            <p className="font-semibold">Please double-check</p>
            {rubricImportSnapshot.warnings.map((warning, idx) => (
              <p key={`import-warning-${idx}`}>&bull; {warning}</p>
            ))}
          </div>
        )}

        <button
          type="button"
          className="text-sm font-semibold text-primary-700 hover:text-primary-900"
          onClick={() => setShowRubricImportPreview(prev => !prev)}
        >
          {showRubricImportPreview ? 'Hide extracted text' : 'Show extracted text used'}
        </button>

        {showRubricImportPreview && (
          <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-xl border border-secondary-200 bg-white p-3 text-xs text-secondary-800">
            {rubricImportSnapshot.preview}
          </pre>
        )}
      </div>
    );
  };

  // --- Exemplar Handlers (Copied as they are unchanged) ---
    const handleAddExemplar = () => {
        const newId = `ex-${exemplars.length}-${Date.now()}`;
        setExemplars([...exemplars, { 
            id: newId, 
            essayText: '', 
            criteriaBands: [{id: `cb-${0}-${newId}`, name: '', band: ''}], 
            overallScore: '', 
            explanation: '',
            stagedImages: [],
        }]);
    };

    const handleRemoveExemplar = (exemplarId: string) => {
        setExemplars(exemplars.filter(ex => ex.id !== exemplarId));
    };
    
    const handleExemplarChange = (exemplarId: string, field: string, value: any) => {
        setExemplars(exemplars.map(ex => ex.id === exemplarId ? { ...ex, [field]: value } : ex));
    };
    
    const handleExemplarImagesChange = (exemplarId: string, images: ExemplarImage[]) => {
        setExemplars(exemplars.map(ex => ex.id === exemplarId ? { ...ex, stagedImages: images } : ex));
    };

    const handleAddExemplarCriterion = (exemplarId: string) => {
        setExemplars(exemplars.map(ex => {
            if (ex.id === exemplarId) {
                const newId = `cb-${ex.criteriaBands.length}-${exemplarId}-${Date.now()}`;
                const newCriteriaBands = [...ex.criteriaBands, {id: newId, name: '', band: ''}];
                return {...ex, criteriaBands: newCriteriaBands};
            }
            return ex;
        }));
    };
    
    const handleRemoveExemplarCriterion = (exemplarId: string, criterionId: string) => {
        setExemplars(exemplars.map(ex => {
            if (ex.id === exemplarId) {
                if (ex.criteriaBands.length > 1) {
                    const newCriteriaBands = ex.criteriaBands.filter(c => c.id !== criterionId);
                    return {...ex, criteriaBands: newCriteriaBands};
                }
            }
            return ex;
        }));
    };

    const handleExemplarCriterionChange = (exemplarId: string, criterionId: string, field: string, value: any) => {
        setExemplars(exemplars.map(ex => {
            if (ex.id === exemplarId) {
                const newCriteriaBands = ex.criteriaBands.map(c => c.id === criterionId ? {...c, [field]: value} : c);
                return {...ex, criteriaBands: newCriteriaBands};
            }
            return ex;
        }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});
    let hasError = false;

    const newErrors: any = {};
    if (!name.trim()) newErrors.name = 'Assessment Name/Title is required.';
    if (!question.trim()) newErrors.question = 'Assessment Question / Task Description is required.';
    if (!yearLevel) newErrors.yearLevel = 'Year Level is required.';
    if (!totalMarks || Number(totalMarks) <= 0) newErrors.totalMarks = 'Total Marks must be a positive number.';
    if (!subject) newErrors.subject = 'Subject is required.';
    if (!examCondition) newErrors.examCondition = 'Exam Condition is required.';
    if (!assessmentType) newErrors.assessmentType = 'Type of Assessment Task is required.';
    if (!minWordCount || Number(minWordCount) <= 0) {
      newErrors.minWordCount = 'Expected Word Count (Min) is required and must be a positive number.';
    }
    if (!maxWordCount || Number(maxWordCount) <= 0) {
      newErrors.maxWordCount = 'Expected Word Count (Max) is required and must be a positive number.';
    } else if (minWordCount && Number(maxWordCount) < Number(minWordCount)) {
      newErrors.maxWordCount = 'Max word count must be greater than or equal to the min word count.';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      hasError = true;
    }

    const totalWeight = criteria.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);
    if (criteria.length > 0 && totalWeight !== 100) {
        newErrors.weights = `The weights for all criteria must sum to 100%. Current sum: ${totalWeight}%.`;
        hasError = true;
    }
    
    const coverageErrors: Array<{ criterionId: string; message: string }> = [];
    criteria.forEach(c => {
        const bands = c.bands.map(b => ({...b, min: Number(b.min), max: Number(b.max)})).sort((a,b) => a.min - b.min);
        if(bands.length === 0) {
            coverageErrors.push({criterionId: c.id, message: "Each criterion must have at least one band."});
            return;
        }
        if(bands[0].min !== 1) {
            coverageErrors.push({criterionId: c.id, message: `The lowest band must start at 1, but it starts at ${bands[0].min}.`});
        }
        if(bands[bands.length - 1].max !== Number(totalMarks)) {
            coverageErrors.push({criterionId: c.id, message: `The highest band must end at the total mark of ${totalMarks}, but it ends at ${bands[bands.length - 1].max}.`});
        }
        for (let i = 0; i < bands.length - 1; i++) {
            if (bands[i].max + 1 !== bands[i+1].min) {
                coverageErrors.push({criterionId: c.id, message: `Gap detected: Band ending at ${bands[i].max} should be followed by a band starting at ${bands[i].max + 1}, but the next one starts at ${bands[i+1].min}.`});
            }
        }
        if (bands.some(b => !b.descriptor.trim())) {
             coverageErrors.push({criterionId: c.id, message: 'All bands must have a descriptor text.'});
        }
    });

    if (coverageErrors.length > 0) {
        newErrors.coverage = coverageErrors;
        hasError = true;
    }

    if (hasError) {
      setValidationErrors(newErrors);
      return;
    }

    setIsSaving(true);
    setSaveStatus(existingAssessment ? 'Saving changes...' : 'Saving assessment...');

    try {
        let processedExemplars = [...exemplars];

        for (let i = 0; i < processedExemplars.length; i++) {
            const exemplar = processedExemplars[i];
            if (exemplar.stagedImages && exemplar.stagedImages.length > 0) {
                let fullText = exemplar.essayText ? `${exemplar.essayText.trim()}\n\n` : '';
                const sortedImages = sortImagesByPageLabel(exemplar.stagedImages);
                
                for (let j = 0; j < sortedImages.length; j++) {
                    const image = sortedImages[j];
                    setSaveStatus(`Transcribing page ${j + 1}/${sortedImages.length} (Exemplar ${i + 1})...`);
                    const transcribedPageText = await transcribeImage(image.base64 || '');
                    fullText += `--- Transcribed from Image (Page: ${image.pageLabel}) ---\n${transcribedPageText}\n\n`;
                }
                
                processedExemplars[i] = { ...exemplar, essayText: fullText.trim() };
            }
        }

        setSaveStatus('Finalizing assessment data...');

        const cleanedExemplars = processedExemplars.map(ex => {
            const { id, stagedImages, ...restOfExemplar } = ex;
            const cleanedCriteriaBands = restOfExemplar.criteriaBands.map(cb => {
                const { id: criterionId, ...restOfCriterion } = cb;
                return restOfCriterion;
            });
            return { ...restOfExemplar, criteriaBands: cleanedCriteriaBands };
        }).filter(ex => ex.essayText.trim() !== '');
        
        const finalCriteria = criteria.map(c => ({
            name: c.name,
            weight: Number(c.weight),
            bands: c.bands.map(b => ({
                min: Number(b.min),
                max: Number(b.max),
                descriptor: b.descriptor
            }))
        }));

        const assessmentPayload = {
            name,
            question,
            yearLevel,
            systemInstructions: systemInstructions.trim(),
            totalMarks: Number(totalMarks),
            criteria: finalCriteria,
            rubric: generateRubricStringFromCriteria(criteria, totalMarks),
            exemplars: cleanedExemplars,
            // Add new fields
            subject,
            topic,
            year,
            examCondition,
            assessmentType,
            // Word count range (required fields, validated above)
            minWordCount: Number(minWordCount),
            maxWordCount: Number(maxWordCount),
            archived: existingAssessment?.archived || false
        };

        if (existingAssessment) {
            await onSave({ ...existingAssessment, ...assessmentPayload });
        } else {
            await onSave(assessmentPayload);
        }
    } catch (err) {
        console.error("Save/Transcription error:", err);
        setError(`Failed to save assessment: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setIsSaving(false);
        setSaveStatus('');
    }
  };

  return (
    <>
    <form onSubmit={handleSubmit} className="max-w-6xl mx-auto space-y-8">
      <div className="bg-white border border-secondary-200 shadow-sm rounded-2xl px-4 py-5 sm:p-6">
        <p className="text-xs uppercase font-semibold text-secondary-500">Assessment set-up</p>
        <h2 className="text-2xl font-bold text-secondary-900">
          {existingAssessment ? 'Edit Assessment Task' : 'Create New Assessment Task'}
        </h2>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <section className="xl:col-span-2 bg-white border border-secondary-200 rounded-2xl shadow-sm p-6 space-y-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-secondary-900">Task basics</h3>
              <p className="text-sm text-secondary-600">Core details for the assessment.</p>
            </div>
            <span className="text-xs text-secondary-500 bg-secondary-50 border border-secondary-200 rounded-full px-3 py-1">
              Required
            </span>
          </div>
          <TextInput label="Assessment Name/Title" id="assessmentName" value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} placeholder="e.g., Term 1 History Essay" required disabled={isSaving} error={validationErrors.name || undefined} />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CustomSelect
              id="subject"
              label="Subject"
              value={subject}
              onChange={(value) => setSubject(value)}
              options={[...DEFAULT_SUBJECTS, ...customValues.subjects.filter(s => !DEFAULT_SUBJECTS.includes(s))]}
              placeholder="Select Subject"
              required
              disabled={isSaving}
              error={validationErrors.subject}
              onAddCustom={(value) => addCustomValue('subject', value)}
            />
            
            <CustomSelect
              id="topic"
              label="Topic"
              value={topic}
              onChange={(value) => setTopic(value)}
              options={[...DEFAULT_TOPICS, ...customValues.topics.filter(t => !DEFAULT_TOPICS.includes(t))]}
              placeholder="e.g., Russian & the Soviet Union"
              disabled={isSaving}
              onAddCustom={(value) => addCustomValue('topic', value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CustomSelect
              id="yearLevel"
              label="Year Level"
              value={yearLevel}
              onChange={(value) => setYearLevel(value)}
              options={DEFAULT_YEAR_GROUPS}
              placeholder="Select Year Level..."
              required
              disabled={isSaving}
              error={validationErrors.yearLevel}
              showAddCustom={false}
            />
            
            <CustomSelect
              id="year"
              label="Year"
              value={year}
              onChange={(value) => setYear(value)}
              options={YEAR_OPTIONS}
              placeholder="Select Year..."
              disabled={isSaving}
              showAddCustom={false}
            />
            
            <TextInput 
              label="Total Marks Possible for Essay" 
              id="totalMarks" 
              type="number" 
              value={totalMarks} 
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotalMarks(e.target.value === '' ? '' : Number(e.target.value))} 
              placeholder="e.g., 20" 
              required 
              disabled={isSaving} 
              error={validationErrors.totalMarks || undefined} 
            />
          </div>

          <TextArea label="Assessment Question / Task Description" id="assessmentQuestion" value={question} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuestion(e.target.value)} rows={3} placeholder="Keep the prompt tight and specific, e.g., Analyze the causes of World War I, focusing on..." required disabled={isSaving} error={validationErrors.question || undefined} />
        </section>

        <section className="bg-white border border-secondary-200 rounded-2xl shadow-sm p-6 space-y-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-secondary-900">Logistics</h3>
              <p className="text-sm text-secondary-600">Set how and where the task is sat.</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <CustomSelect
                id="examCondition"
                label="Exam Condition"
                value={examCondition}
                onChange={(value) => setExamCondition(value)}
                options={[...DEFAULT_EXAM_CONDITIONS, ...customValues.examConditions.filter(c => !DEFAULT_EXAM_CONDITIONS.includes(c))]}
                placeholder="Select Exam Condition"
                required
                disabled={isSaving}
                error={validationErrors.examCondition}
                onAddCustom={(value) => addCustomValue('examCondition', value)}
              />
              
              <CustomSelect
                id="assessmentType"
                label="Type of Assessment Task"
                value={assessmentType}
                onChange={(value) => setAssessmentType(value)}
                options={[...DEFAULT_ASSESSMENT_TYPES, ...customValues.assessmentTypes.filter(t => !DEFAULT_ASSESSMENT_TYPES.includes(t))]}
                placeholder="Select Assessment Type"
                required
                disabled={isSaving}
                error={validationErrors.assessmentType}
                onAddCustom={(value) => addCustomValue('assessmentType', value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                label="Expected Word Count (Min)"
                id="minWordCount"
                type="number"
                value={minWordCount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinWordCount(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="e.g., 800"
                required
                disabled={isSaving}
                error={validationErrors.minWordCount || undefined}
              />
              <TextInput
                label="Expected Word Count (Max)"
                id="maxWordCount"
                type="number"
                value={maxWordCount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxWordCount(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="e.g., 1200"
                required
                disabled={isSaving}
                error={validationErrors.maxWordCount || undefined}
              />
            </div>
          </div>

        </section>
      </div>

      <section className="bg-white border border-secondary-200 rounded-2xl shadow-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900">Marking rubric & criteria</h3>
            <p className="text-sm text-secondary-600">Choose the path that suits how you capture your rubric.</p>
          </div>
          {rubricMode !== 'choice' && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setRubricMode('choice')}
            >
              &lt; Choose a different option
            </Button>
          )}
        </div>

        {rubricMode === 'choice' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 shadow-soft">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-blue-600 font-bold shadow-soft text-lg">
                !
              </div>
              <p className="text-sm sm:text-base font-semibold text-blue-900">
                To mark accurately, the AI needs the rubric in a specific format.
              </p>
            </div>

            <div className="flex flex-col md:flex-row md:items-stretch md:justify-center gap-4 md:gap-6">
              <button
                type="button"
                onClick={() => setRubricMode('manual')}
                className="flex-1 text-left p-5 rounded-2xl border-2 border-blue-200 bg-white hover:border-blue-400 hover:shadow-md transition-all min-w-[260px]"
              >
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 justify-center sm:justify-start">
                  <img
                    src="https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/Media%2Fmanual%20input.png?alt=media&token=a5c7e9ad-7153-404f-8ee8-f1ea115a56f1"
                    alt="Manual rubric setup"
                    className="h-16 w-16 sm:h-24 sm:w-24 object-contain drop-shadow-sm"
                  />
                  <div className="text-center sm:text-left">
                    <p className="text-xs font-bold uppercase tracking-wide text-blue-600 mb-1">Manual setup</p>
                    <h4 className="text-xl sm:text-2xl font-extrabold text-midnight mb-1">Set up rubric manually</h4>
                    <p className="text-sm sm:text-base text-secondary-700">
                      Type each criterion, descriptor and weighting yourself.
                    </p>
                    <span className="mt-3 inline-flex items-center text-sm font-semibold text-blue-600">
                      Start manual setup &gt;
                    </span>
                  </div>
                </div>
              </button>

              <div className="flex items-center justify-center">
                <span className="px-2 text-2xl font-bold text-gray-400">OR</span>
              </div>

              <button
                type="button"
                onClick={() => setRubricMode('upload')}
                className="flex-1 text-left p-5 rounded-2xl border-2 border-orange-200 bg-white hover:border-orange-300 hover:shadow-md transition-all min-w-[260px]"
              >
                <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3 sm:gap-4 justify-center sm:justify-start">
                  <img
                    src="https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/Media%2Fai%20assisted.png?alt=media&token=60d231a4-988f-4b64-8fdc-2b103b9aa34a"
                    alt="AI-assisted rubric upload"
                    className="h-20 w-20 sm:h-28 sm:w-28 object-contain drop-shadow-sm"
                  />
                  <div className="text-center sm:text-left">
                    <p className="text-xs font-bold uppercase tracking-wide text-orange-600 mb-1">AI-assisted</p>
                    <h4 className="text-xl sm:text-2xl font-extrabold text-midnight mb-1">Upload rubric and let AI configure</h4>
                    <p className="text-sm sm:text-base text-secondary-700">
                      Upload a PDF, Word doc, scan or photo and our AI will convert it into the required format.
                    </p>
                    <span className="mt-3 inline-flex items-center text-sm font-semibold text-orange-600">
                      Upload rubric & convert &gt;
                    </span>
                  </div>
                </div>
              </button>

              <div className="flex items-center justify-center">
                <span className="px-2 text-2xl font-bold text-gray-400">OR</span>
              </div>

              <button
                type="button"
                onClick={() => rubricTemplates.length > 0 && setRubricMode('template')}
                disabled={rubricTemplates.length === 0}
                className={cn(
                  "flex-1 text-left p-5 rounded-2xl border-2 bg-white transition-all min-w-[260px]",
                  rubricTemplates.length > 0
                    ? "border-emerald-200 hover:border-emerald-400 hover:shadow-md"
                    : "border-secondary-200 opacity-60 cursor-not-allowed"
                )}
              >
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 justify-center sm:justify-start">
                  <div className="flex h-16 w-16 sm:h-24 sm:w-24 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <LayoutTemplate className="h-10 w-10 sm:h-14 sm:w-14" />
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-600 mb-1">Saved template</p>
                    <h4 className="text-xl sm:text-2xl font-extrabold text-midnight mb-1">Use a saved template</h4>
                    <p className="text-sm sm:text-base text-secondary-700">
                      {rubricTemplates.length > 0
                        ? 'Apply a marking rubric you’ve saved from a previous assessment.'
                        : 'No templates yet — save one from an existing assessment first.'}
                    </p>
                    {rubricTemplates.length > 0 && (
                      <span className="mt-3 inline-flex items-center text-sm font-semibold text-emerald-600">
                        Choose a template &gt;
                      </span>
                    )}
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {rubricMode === 'manual' && (
          <div className="mt-6 space-y-4">
            {renderCriteriaHeader(
              'Enter your marking rubric manually',
              'Define each criterion, weighting, and its band descriptors.'
            )}
            {renderImportSnapshot()}
            {renderCriteriaErrorsAndList()}
          </div>
        )}

        {rubricMode === 'template' && (
          <div className="mt-6 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 className="text-lg font-semibold text-secondary-900">Apply a saved rubric template</h4>
                <p className="text-sm text-secondary-600">
                  Applying copies the template into this assessment — you can edit it freely afterwards
                  without changing the template.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsManagingTemplates(prev => !prev);
                  cancelRenameTemplate();
                }}
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary-700 hover:text-primary-900"
              >
                <Settings className="h-4 w-4" />
                {isManagingTemplates ? 'Done managing' : 'Manage templates'}
              </button>
            </div>

            <div className="max-w-xl space-y-2">
              <label htmlFor="rubric-template-select" className="text-sm font-medium text-foreground">
                Template
              </label>
              <Select
                id="rubric-template-select"
                value={selectedTemplateId}
                onValueChange={setSelectedTemplateId}
                placeholder="Select a template..."
                disabled={isSaving}
              >
                {rubricTemplates.map(template => (
                  <SelectItem key={template.id} value={template.id}>
                    {`${template.name} — ${template.criteria.length} criteri${template.criteria.length === 1 ? 'on' : 'a'}, ${template.totalMarks} marks`}
                  </SelectItem>
                ))}
              </Select>
            </div>

            {selectedTemplate && (
              <div className="rounded-2xl border border-secondary-200 bg-secondary-50/80 p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-secondary-900">{selectedTemplate.name}</p>
                    <p className="text-sm text-secondary-600">Total marks: {selectedTemplate.totalMarks}</p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleApplyTemplateClick}
                    variant="primary"
                    disabled={isSaving}
                    className="gap-1.5"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Apply Template
                  </Button>
                </div>
                <ul className="space-y-1 text-sm text-secondary-800">
                  {selectedTemplate.criteria.map((c, idx) => (
                    <li key={`tpl-crit-${idx}`} className="flex justify-between gap-3 border-b border-secondary-100 pb-1 last:border-b-0">
                      <span>{c.name}</span>
                      <span className="text-secondary-500 whitespace-nowrap">{c.weight}% · {c.bands.length} band{c.bands.length !== 1 ? 's' : ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isManagingTemplates && (
              <div className="rounded-2xl border border-secondary-200 bg-white p-4 space-y-2">
                <p className="text-sm font-semibold text-secondary-900">Your templates</p>
                {templateManageError && (
                  <p className="text-sm text-red-600" role="alert">{templateManageError}</p>
                )}
                {rubricTemplates.length === 0 ? (
                  <p className="text-sm text-secondary-600">No templates saved yet.</p>
                ) : (
                  <ul className="divide-y divide-secondary-100">
                    {rubricTemplates.map(template => (
                      <li key={template.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                        {editingTemplateId === template.id ? (
                          <div className="flex flex-1 items-center gap-2 min-w-[240px]">
                            <input
                              type="text"
                              value={editingTemplateName}
                              onChange={(e) => {
                                setEditingTemplateName(e.target.value.slice(0, 80));
                                setTemplateManageError(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); submitRenameTemplate(); }
                                if (e.key === 'Escape') cancelRenameTemplate();
                              }}
                              className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                              autoFocus
                            />
                            <Button type="button" size="sm" onClick={submitRenameTemplate} className="h-8 px-2" aria-label="Save new name">
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button type="button" size="sm" variant="ghost" onClick={cancelRenameTemplate} className="h-8 px-2" aria-label="Cancel rename">
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="min-w-[200px]">
                              <p className="text-sm font-medium text-secondary-900">{template.name}</p>
                              <p className="text-xs text-secondary-500">
                                {template.criteria.length} criteri{template.criteria.length === 1 ? 'on' : 'a'} · {template.totalMarks} marks
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button type="button" size="sm" variant="ghost" onClick={() => startRenameTemplate(template)} className="h-8 gap-1 px-2" aria-label={`Rename template ${template.name}`}>
                                <Pencil className="h-3.5 w-3.5" />
                                Rename
                              </Button>
                              <Button type="button" size="sm" variant="ghost" onClick={() => setTemplateToDelete(template)} className="h-8 gap-1 px-2 text-red-600 hover:text-red-700" aria-label={`Delete template ${template.name}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                              </Button>
                            </div>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {rubricMode === 'upload' && (
          <div className="mt-4 space-y-6">
            <RubricUploadAssistant
              totalMarks={totalMarks}
              subject={subject}
              yearLevel={yearLevel}
              topic={topic}
              assessmentType={assessmentType}
              onRubricReady={handleRubricUploadResult}
              isDisabled={isSaving}
            />
            {renderImportSnapshot()}
            <div>
              {renderCriteriaHeader(
                'Review & edit the generated rubric',
                'Adjust the imported details, weights, and descriptors below.'
              )}
              {renderCriteriaErrorsAndList()}
            </div>
          </div>
        )}
      </section>

      <div className="flex flex-wrap justify-between gap-3 pt-2">
        <Button type="button" onClick={onCancel} variant="secondary" disabled={isSaving}>Cancel</Button>
        <Button 
          type="submit" 
          disabled={isSaving} 
          isLoading={isSaving} 
          loadingText={saveStatus} 
          variant="primary" 
          className="gradient-bg-primary shadow-soft"
        >
          {existingAssessment ? 'Save Changes' : 'Save Assessment'}
        </Button>
      </div>
    </form>

    {/* Modals are rendered outside the <form> so their buttons never trigger form submission */}
    <ConfirmModal
      isOpen={showApplyConfirm}
      onClose={() => setShowApplyConfirm(false)}
      onConfirm={() => {
        setShowApplyConfirm(false);
        if (selectedTemplate) applyTemplate(selectedTemplate);
      }}
      title="Replace current rubric?"
      message={`Applying "${selectedTemplate?.name || 'this template'}" will replace the criteria and total marks currently in the form. Continue?`}
      confirmButtonText="Apply Template"
      loadingText="Applying..."
      variant="default"
    />

    <ConfirmModal
      isOpen={!!templateToDelete}
      onClose={() => setTemplateToDelete(null)}
      onConfirm={confirmDeleteTemplate}
      title="Delete template?"
      message={`Delete template "${templateToDelete?.name || ''}"? Assessments already created from it are not affected.`}
      isLoading={isDeletingTemplate}
      confirmButtonText="Delete Template"
      loadingText="Deleting..."
    />

    <SaveRubricTemplateModal
      isOpen={showSaveTemplateModal}
      onClose={() => setShowSaveTemplateModal(false)}
      defaultName={name}
      totalMarks={Number(totalMarks) || 0}
      criteria={templateCriteriaFromForm}
      sourceAssessmentId={existingAssessment?.id}
    />
    </>
  );
};

export default CreateAssessmentForm;
